import React, { useState, useEffect } from 'react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { 
  TrendingUp, 
  Users, 
  BookOpen, 
  Headphones, 
  Video,
  Quote, 
  Calendar,
  BarChart3
} from 'lucide-react';
import { analyticsService, AnalyticsData } from '../services/analyticsService';
import StatsCard from '../components/dashboard/StatsCard';

const Analytics = () => {
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchAnalyticsData();
  }, []);

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      const data = await analyticsService.getAnalyticsData();
      setAnalyticsData(data);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch analytics data');
    } finally {
      setLoading(false);
    }
  };

  // Colors for charts
  const colors = {
    quotes: '#3B82F6', // Blue
    ebooks: '#10B981', // Green
    audio: '#F59E0B',  // Yellow
    videos: '#06B6D4', // Cyan
    users: '#EF4444',   // Red
    published: '#8B5CF6' // Purple
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          <span className="ml-3 text-gray-600">Loading analytics...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="card">
          <div className="card-body text-center py-12">
            <div className="text-red-500 mb-4">
              <BarChart3 className="w-12 h-12 mx-auto" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Loading Analytics</h3>
            <p className="text-gray-600 mb-4">{error}</p>
            <button 
              onClick={fetchAnalyticsData}
              className="btn btn-primary"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!analyticsData) {
    return (
      <div className="space-y-6">
        <div className="card">
          <div className="card-body text-center py-12">
            <div className="text-gray-500 mb-4">
              <BarChart3 className="w-12 h-12 mx-auto" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Data Available</h3>
            <p className="text-gray-600">Analytics data is not available at the moment.</p>
          </div>
        </div>
      </div>
    );
  }

  // Prepare data for pie chart (total content distribution)
  const contentDistribution = [
    { name: 'Quotes', value: analyticsData.totalQuotes, color: colors.quotes },
    { name: 'E-books', value: analyticsData.totalEbooks, color: colors.ebooks },
    { name: 'Audio', value: analyticsData.totalAudio, color: colors.audio },
    { name: 'Videos', value: analyticsData.totalVideos, color: colors.videos }
  ];

  return (
    <div className="space-y-4">
      {/* Page Header - Compact */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900 font-heading text-primary-800">Analytics</h1>
          <p className="text-sm text-gray-600 mt-0.5">
            Content growth and user engagement insights
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <Calendar className="w-3.5 h-3.5" />
          Last 6 months
        </div>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
        <StatsCard 
          title="Total Quotes" 
          value={analyticsData.totalQuotes} 
          subtitle={`${analyticsData.publishedQuotes} published`}
          icon={Quote} 
          color="blue" 
        />
        <StatsCard 
          title="Total E-books" 
          value={analyticsData.totalEbooks} 
          subtitle={`${analyticsData.publishedEbooks} published`}
          icon={BookOpen} 
          color="green" 
        />
        <StatsCard 
          title="Total Audio" 
          value={analyticsData.totalAudio} 
          subtitle={`${analyticsData.publishedAudio} published`}
          icon={Headphones} 
          color="yellow" 
        />
        <StatsCard
          title="Total Videos"
          value={analyticsData.totalVideos}
          subtitle={`${analyticsData.publishedVideos} published`}
          icon={Video}
          color="cyan"
        />
        <StatsCard 
          title="Total Users" 
          value={analyticsData.totalUsers} 
          subtitle="Registered users"
          icon={Users} 
          color="red" 
        />
      </div>

      {/* Monthly Content Growth Chart */}
      <div className="card">
        <div className="card-body">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-base font-semibold text-gray-900">Monthly Content Growth</h3>
              <p className="text-xs text-gray-600">New content added per month</p>
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <TrendingUp className="w-3.5 h-3.5" />
              Last 6 months
            </div>
          </div>
          
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={analyticsData.monthlyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}
                />
                <Legend />
                <Bar dataKey="quotes" fill={colors.quotes} name="Quotes" />
                <Bar dataKey="ebooks" fill={colors.ebooks} name="E-books" />
                <Bar dataKey="audio" fill={colors.audio} name="Audio" />
                <Bar dataKey="videos" fill={colors.videos} name="Videos" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* User Onboarding Chart */}
      <div className="card">
        <div className="card-body">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-base font-semibold text-gray-900">User Onboarding</h3>
              <p className="text-xs text-gray-600">New users registered per month</p>
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <Users className="w-3.5 h-3.5" />
              Last 6 months
            </div>
          </div>
          
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={analyticsData.monthlyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="users" 
                  stroke={colors.users} 
                  strokeWidth={3}
                  name="New Users"
                  dot={{ fill: colors.users, strokeWidth: 2, r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Content Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card">
          <div className="card-body">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-base font-semibold text-gray-900">Content Distribution</h3>
                <p className="text-xs text-gray-600">Total content by type</p>
              </div>
            </div>
            
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={contentDistribution}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {contentDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-base font-semibold text-gray-900">Publishing Status</h3>
                <p className="text-xs text-gray-600">Content publication overview</p>
              </div>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                <div className="flex items-center">
                  <Quote className="w-4 h-4 text-blue-600 mr-2" />
                  <span className="font-medium text-sm text-gray-900">Quotes</span>
                </div>
                <div className="text-right">
                  <div className="text-base font-semibold text-gray-900">
                    {analyticsData.publishedQuotes}/{analyticsData.totalQuotes}
                  </div>
                  <div className="text-xs text-gray-600">
                    {analyticsData.totalQuotes > 0 
                      ? Math.round((analyticsData.publishedQuotes / analyticsData.totalQuotes) * 100)
                      : 0}% published
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                <div className="flex items-center">
                  <BookOpen className="w-4 h-4 text-green-600 mr-2" />
                  <span className="font-medium text-sm text-gray-900">E-books</span>
                </div>
                <div className="text-right">
                  <div className="text-base font-semibold text-gray-900">
                    {analyticsData.publishedEbooks}/{analyticsData.totalEbooks}
                  </div>
                  <div className="text-xs text-gray-600">
                    {analyticsData.totalEbooks > 0 
                      ? Math.round((analyticsData.publishedEbooks / analyticsData.totalEbooks) * 100)
                      : 0}% published
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                <div className="flex items-center">
                  <Headphones className="w-4 h-4 text-yellow-600 mr-2" />
                  <span className="font-medium text-sm text-gray-900">Audio</span>
                </div>
                <div className="text-right">
                  <div className="text-base font-semibold text-gray-900">
                    {analyticsData.publishedAudio}/{analyticsData.totalAudio}
                  </div>
                  <div className="text-xs text-gray-600">
                    {analyticsData.totalAudio > 0 
                      ? Math.round((analyticsData.publishedAudio / analyticsData.totalAudio) * 100)
                      : 0}% published
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between p-3 bg-cyan-50 rounded-lg">
                <div className="flex items-center">
                  <Video className="w-4 h-4 text-cyan-600 mr-2" />
                  <span className="font-medium text-sm text-gray-900">Videos</span>
                </div>
                <div className="text-right">
                  <div className="text-base font-semibold text-gray-900">
                    {analyticsData.publishedVideos}/{analyticsData.totalVideos}
                  </div>
                  <div className="text-xs text-gray-600">
                    {analyticsData.totalVideos > 0
                      ? Math.round((analyticsData.publishedVideos / analyticsData.totalVideos) * 100)
                      : 0}% published
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics;
